import { MixpanelEvent } from "#types/tracking.ts";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { toast } from "react-toastify";
import { useSharedTranslation } from "./useSharedTranslation.ts";
import { useTracking } from "./useTracking.ts";

/**
 * Custom hook to handle OAuth-related query parameters and display appropriate toast notifications.
 *
 * This hook monitors URL query parameters for OAuth success/error states and displays
 * localized toast messages accordingly. It prevents duplicate toasts by using toast IDs.
 *
 * Expected URL parameters:
 * - connectorType: The type of connector (e.g., "UserJira")
 * - oAuthSuccess: "true" if OAuth was successful
 * - oAuthError: Present if OAuth failed
 * - oAuthErrorDescription: Optional error description
 *
 * @example
 * // Success URL: /?oAuthSuccess=true&connectorType=UserJira
 * // Error URL: /?oAuthError=true&connectorType=UserJira&oAuthErrorDescription=Invalid%20credentials
 */
export const useOAuthToast = () => {
    const [searchParams] = useSearchParams();
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();

    useEffect(() => {
        const connectorType = searchParams.get("connectorType");
        if (!connectorType) {
            return;
        }

        // Get the display name for the connector type, fallback to the raw value
        const displayName = tShared(`oauth.connectorTypes.${connectorType}`, connectorType);

        // Check for OAuth success (case-insensitive)
        if (searchParams.get("oAuthSuccess")?.toLowerCase() === "true") {
            const toastId = `oauth-success-${connectorType}`;
            if (!toast.isActive(toastId)) {
                trackEvent(MixpanelEvent.ButtonClicked, {
                    Location: "OAuth Callback",
                    Trigger: `Connect to ${displayName}`,
                    Status: "Success"
                });

                toast.success(tShared("oauth.connectionSuccess", { connectorType: displayName }), {
                    toastId
                });
            }

            return;
        }

        // Check for OAuth error
        const oAuthError = searchParams.get("oAuthError");
        if (oAuthError) {
            const errorDescription = searchParams.get("oAuthErrorDescription");
            const toastId = `oauth-error-${connectorType}-${oAuthError}`;

            if (!toast.isActive(toastId)) {
                trackEvent(MixpanelEvent.ButtonClicked, {
                    Location: "OAuth Callback",
                    Trigger: `Connect to ${displayName}`,
                    Status: "Failed"
                });

                if (errorDescription) {
                    toast.error(tShared("oauth.connectionErrorWithDescription", {
                        connectorType: displayName,
                        errorDescription
                    }), { toastId });
                } else {
                    toast.error(tShared("oauth.connectionError", { connectorType: displayName }), {
                        toastId
                    });
                }
            }
        }
    }, [searchParams, tShared, trackEvent]);
};
